import { formatISO } from "date-fns";
import format from "date-fns/format";
import { action, flow, observable } from "mobx";
import { observer, Observer } from "mobx-react";
import { Button } from "primereact/button";
import React, { useContext } from "react";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import { AddCourseToCandidateResponse } from "../../../../api/candidate/addCourseToCandidate";
import { useLocStore } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import { redirect } from "../../../../WSUtils/urlUtils";
import { invalidateQueries } from "../../../../WSUtils/useLatestQuery";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { concatClassnames, defDFnsOpt } from "../../../../WSUtils/util";
import {
  CVPSearchParams,
  queryStringStore,
} from "../../../../WSUtils/wsQueryStringStore";
import { CVEditContext } from "../../CVEditContext";
import { CVEditListStyles } from "../../CVEditListStyles";
import { CVEditCourse } from "./CVEditCourse";
import { onCveCourseEditorCloseButtonClick } from "./CVEditCourseEditor";
import { CVEditCourseSegmentStore } from "./CVEditCourseSegmentStore";

export interface CVEditCourseListProps {
  courseSegmentStore: CVEditCourseSegmentStore;
}
export const CVEditCourseList = observer(
  ({ courseSegmentStore }: CVEditCourseListProps) => {
    const cvEditContext = useContext(CVEditContext);
    const userInfo = useContext(UserInfoContext);
    const queryClient = useQueryClient();
    const L = useLocStore(LocContext.CV);

    const deletedItems = courseSegmentStore.deletedListItems;
    const items = courseSegmentStore.listItems;

    console.log(courseSegmentStore.listItems);

    if (courseSegmentStore.nTotalListItems === 0) return null;

    const deletedItemsComponent = (
      <Observer
        render={() => (
          <>
            {deletedItems.map((cveCourse: CVEditCourse) => (
              <div
                key={cveCourse.id}
                className={concatClassnames([
                  "cve-listing",
                  "cve-listing-deleted",
                ])}
              >
                <div className="p-inline-message p-component p-inline-message-info">
                  <div aria-live="polite" role="alert">
                    <span className="p-inline-message-icon pi pi-info-circle"></span>
                    <span className="p-inline-message-text">
                      {L.id("122aded32b0f").loc("Slettet")}
                    </span>
                  </div>
                  <Button
                    className="p-button-text"
                    label={L.id("059fb245e18d").loc("Angre")}
                    onClick={(evt) => {
                      evt.preventDefault();

                      const restore = flow(function* () {
                        const result: AddCourseToCandidateResponse =
                          yield userInfo.fullAPI.candidateAPI.addCourseToCandidate(
                            {
                              showSentryDialogOnError: true,
                              userInfo,
                              firmId: userInfo.idFirm,
                              candidateId: userInfo.idCandidate,

                              comment: cveCourse.description,
                              from: cveCourse.from
                                ? formatISO(cveCourse.from)
                                : null,
                              to: cveCourse.to ? formatISO(cveCourse.to) : null,
                              courseType: 0,
                              expirationDate: null,
                              organizer: null,
                              level: 0,

                              //
                              duplicateCheck: false,
                              references: null,
                            }
                          );

                        if (result.ok) {
                          yield invalidateQueries(queryClient, [
                            "candidateAPI.getCoursesForCandidate",
                          ]);

                          courseSegmentStore.deletedCourses.delete(
                            cveCourse.id
                          );
                        }
                      });

                      restore();

                      return false;
                    }}
                  />
                </div>
              </div>
            ))}
          </>
        )}
      />
    );

    const itemsComponent = (
      <Observer
        render={() => (
          <>
            {items.map((cveCourse) => (
              <div
                className={concatClassnames(["cve-listing"])}
                key={cveCourse.id}
              >
                <div className="cve-listing-left">
                  <div className="cve-course-listing-dates cve-left-dates">
                    <span className="cve-course-listing-startdate cve-listing-text">
                      {!!cveCourse.from &&
                        format(cveCourse.from, "MM.yyyy", defDFnsOpt)}
                    </span>
                    <span> - </span>
                    <div className="cve-course-listing-enddate cve-listing-text">
                      {!!cveCourse.to &&
                        format(cveCourse.to, "MM.yyyy", defDFnsOpt)}
                      {!cveCourse.to &&
                        L.id("616608c32aa2").loc("Pågående kurs")}
                    </div>
                  </div>
                </div>
                <div className="cve-listing-right">
                  <div className="cve-listing-buttons">
                    <Buttons
                      cveCourse={cveCourse}
                      courseSegmentStore={courseSegmentStore}
                    />
                  </div>
                  <div
                    className="cve-course-listing-type cve-listing-text"
                    title={L.id("c8500a1d7a02").loc("Kurstype")}
                  >
                    {cveCourse.courseType}
                  </div>
                  <div
                    className="cve-course-listing-organizer cve-listing-text"
                    title={L.id("cb6c2f204dd0").loc("Arrangør")}
                  >
                    {cveCourse.arranger}
                  </div>
                  <div
                    className="cve-course-listing-description cve-listing-text"
                    title={L.id("a8e9ff57d13e").loc("Beskrivelse")}
                  >
                    {cveCourse.description}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      />
    );

    return (
      <CVEditCourseListStyles
        className={concatClassnames([
          "cve-list cve-course-list cve-two-column-list",
          cvEditContext.layoutClassName,
        ])}
      >
        {deletedItemsComponent}
        {itemsComponent}
      </CVEditCourseListStyles>
    );
  }
);

interface ButtonsProps {
  cveCourse: CVEditCourse;
  courseSegmentStore: CVEditCourseSegmentStore;
}

const Buttons = ({ cveCourse, courseSegmentStore }: ButtonsProps) => {
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);
  const L = useLocStore(LocContext.CV);

  const queryClient = useQueryClient();

  const savingUsingToggleButton = observable.box(false);

  return (
    <Observer
      render={() => {
        const isEditingThis =
          courseSegmentStore.editingCourseId === cveCourse.id;

        return (
          <>
            <Button
              icon="pi pi-trash"
              title={L.id("8a1f5a8af021").loc("Fjern kurs")}
              className="p-button-rounded p-button-text p-button-icon-only"
              onClick={flow(function* (evt) {
                evt.preventDefault();

                courseSegmentStore.deletedCourses.set(cveCourse.id, cveCourse);

                if (
                  queryStringStore.qs[CVPSearchParams.cvpec] === cveCourse.id
                ) {
                  redirect(null, {
                    userInfo,
                    mergeQs: { [CVPSearchParams.cvpec]: null },
                  });
                }

                const result =
                  yield userInfo.fullAPI.candidateAPI.removeCourseFromCandidate(
                    {
                      showSentryDialogOnError: true,
                      courseId: Number(cveCourse.id),
                      firmId: userInfo.idFirm,
                      userInfo,
                    }
                  );

                yield invalidateQueries(queryClient, [
                  "candidateAPI.getCoursesForCandidate",
                ]);

                return false;
              })}
            />
            <Button
              icon="pi pi-pencil"
              title={L.id("e7de4768eb00").loc("Rediger kurs")}
              className={concatClassnames([
                "p-button-rounded p-button-icon-only",
                !isEditingThis && "p-button-text",
              ])}
              loading={savingUsingToggleButton.get()}
              onClick={action((evt) => {
                evt.preventDefault();

                if (isEditingThis) {
                  const courseEditorStore =
                    courseSegmentStore.courseEditorStore;

                  onCveCourseEditorCloseButtonClick({
                    courseSegmentStore,
                    courseEditorStore,
                    queryClient,
                    onSaveApiCallStart: () => {
                      savingUsingToggleButton.set(true);
                    },
                    onSaveApiCallEnd: () => {
                      savingUsingToggleButton.set(false);
                    },
                  });
                } else {
                  redirect(null, {
                    userInfo,
                    mergeQs: { [CVPSearchParams.cvpec]: cveCourse.id },
                  });
                }

                return false;
              })}
            />
          </>
        );
      }}
    />
  );
};

export const CVEditCourseListStyles = styled(CVEditListStyles)`
  &.cve-course-list {
    .cve-course-listing-type {
      font-weight: bold;
    }
  }
`;
